import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { Plus } from "lucide-react";

import { productsService } from "../../../services/products";
import { adminProductsService } from "../../../services/adminProducts";
import { api } from "../../../services/api";
import { formatApiError } from "../../../services/api";
import { useAuth } from "../../../app/providers/AuthProvider";
import { hasPermission, Permission } from "../../../app/permissions";
import { usePersistedFilters } from "../../../hooks/usePersistedFilters";
import { EmptyState } from "../../../components/feedback/EmptyState";
import { ErrorState } from "../../../components/feedback/ErrorState";
import { ProductCard, ProductCardSkeleton } from "../components/ProductCard";
import { ProductFilters } from "../components/ProductFilters";
import { ProductDetailModal } from "../components/ProductDetailModal";
import { ProductFormModal } from "../components/ProductFormModal";
import { ConfirmDeleteModal } from "../../../components/admin/ConfirmDeleteModal";
import { Button } from "../../../components/ui/button";

const PAGE_SIZE = 20;

function splitCSV(v) {
  return v ? v.split(",").map((s) => s.trim()).filter(Boolean) : [];
}

export default function ProductsPage() {
  const { t } = useTranslation();
  const { user } = useAuth();
  const canManage = hasPermission(user, Permission.ADMIN_PRODUCTS_MANAGE);
  const [searchParams, setSearchParams] = useSearchParams();

  // Admin form + delete state
  const [formOpen, setFormOpen] = useState(false);
  const [editingProduct, setEditingProduct] = useState(null);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deleting, setDeleting] = useState(false);

  // Bootstrap state from URL.
  const [searchInput, setSearchInput] = useState(searchParams.get("q") || "");
  const [debouncedSearch, setDebouncedSearch] = useState(
    searchParams.get("q") || ""
  );
  const [selectedCategories, setSelectedCategories] = useState(
    splitCSV(searchParams.get("categories"))
  );
  const [selectedBrands, setSelectedBrands] = useState(
    splitCSV(searchParams.get("brands"))
  );

  const [facets, setFacets] = useState({ categories: [], brands: [] });
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const [selected, setSelected] = useState(null);
  const [detailOpen, setDetailOpen] = useState(false);

  const debounceRef = useRef(null);

  // Debounce search input (300ms).
  useEffect(() => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => {
      setDebouncedSearch(searchInput);
      setPage(1);
    }, 300);
    return () => debounceRef.current && clearTimeout(debounceRef.current);
  }, [searchInput]);

  // Load facets from master data (Bug 3): every entry regardless of
  // whether products actually reference it. Falls back to product-derived
  // list if the master-data endpoints fail.
  useEffect(() => {
    Promise.all([
      api.get("/categories").then((r) => r.data.items || []),
      api.get("/brands").then((r) => r.data.items || []),
    ])
      .then(([cats, brs]) => {
        const byLocale = (a, b) => a.localeCompare(b, undefined, { sensitivity: "base" });
        setFacets({
          categories: cats.map((c) => c.name).sort(byLocale),
          brands: brs.map((b) => b.name).sort(byLocale),
        });
      })
      .catch(() =>
        productsService
          .facets()
          .then(setFacets)
          .catch(() => setFacets({ categories: [], brands: [] }))
      );
  }, []);

  // Keep URL in sync with active filters — shareable state, cheap win.
  useEffect(() => {
    const next = new URLSearchParams();
    if (debouncedSearch) next.set("q", debouncedSearch);
    if (selectedCategories.length) next.set("categories", selectedCategories.join(","));
    if (selectedBrands.length) next.set("brands", selectedBrands.join(","));
    setSearchParams(next, { replace: true });
  }, [debouncedSearch, selectedCategories, selectedBrands, setSearchParams]);

  const loadProducts = useCallback(
    async (nextPage = 1, replace = true) => {
      setLoading(true);
      setError(null);
      try {
        const data = await productsService.list({
          search: debouncedSearch || undefined,
          category: selectedCategories.length ? selectedCategories.join(",") : undefined,
          brand: selectedBrands.length ? selectedBrands.join(",") : undefined,
          page: nextPage,
          page_size: PAGE_SIZE,
        });
        setTotal(data.total);
        setPage(data.page);
        setItems((prev) => (replace ? data.items : [...prev, ...data.items]));
      } catch (err) {
        setError(formatApiError(err, t("common.error_generic")));
      } finally {
        setLoading(false);
      }
    },
    [debouncedSearch, selectedCategories, selectedBrands, t]
  );

  useEffect(() => {
    loadProducts(1, true);
  }, [loadProducts]);

  // Bug 3: when the user returns to /products after switching tabs / logging
  // in as a different role, refetch so admin edits made elsewhere become
  // visible without a manual reload. `visibilitychange` catches OS-tab
  // switches; `focus` catches window-level focus regains. Cheap; no cache.
  useEffect(() => {
    const refresh = () => {
      if (document.visibilityState === "visible") loadProducts(1, true);
    };
    document.addEventListener("visibilitychange", refresh);
    window.addEventListener("focus", refresh);
    return () => {
      document.removeEventListener("visibilitychange", refresh);
      window.removeEventListener("focus", refresh);
    };
  }, [loadProducts]);

  // A16: persist filter state per user + route. Excludes pagination page.
  const persistedFilters = usePersistedFilters({
    route: "products",
    userId: user?.id,
    snapshot: { searchInput, selectedCategories, selectedBrands },
    apply: (snap) => {
      if (typeof snap.searchInput === "string") {
        setSearchInput(snap.searchInput);
        setDebouncedSearch(snap.searchInput);
      }
      if (Array.isArray(snap.selectedCategories)) setSelectedCategories(snap.selectedCategories);
      if (Array.isArray(snap.selectedBrands)) setSelectedBrands(snap.selectedBrands);
    },
  });

  const isFiltered = useMemo(
    () =>
      Boolean(
        debouncedSearch || selectedCategories.length || selectedBrands.length
      ),
    [debouncedSearch, selectedCategories, selectedBrands]
  );

  const toggleCategory = (c) => {
    setSelectedCategories((prev) =>
      prev.includes(c) ? prev.filter((x) => x !== c) : [...prev, c]
    );
    setPage(1);
  };
  const toggleBrand = (b) => {
    setSelectedBrands((prev) =>
      prev.includes(b) ? prev.filter((x) => x !== b) : [...prev, b]
    );
    setPage(1);
  };

  const clearFilters = () => {
    setSearchInput("");
    setDebouncedSearch("");
    setSelectedCategories([]);
    setSelectedBrands([]);
    persistedFilters.clear();
  };

  const openProduct = (p) => {
    setSelected(p);
    setDetailOpen(true);
  };

  // Auto-open the create form when ?new=1 is present (from Quick Actions
  // tile). Consumes the flag so a refresh doesn't re-trigger it.
  useEffect(() => {
    if (!canManage) return;
    if (searchParams.get("new") === "1") {
      setEditingProduct(null);
      setFormOpen(true);
      const next = new URLSearchParams(searchParams);
      next.delete("new");
      setSearchParams(next, { replace: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [canManage, searchParams]);

  const openCreate = () => {
    setEditingProduct(null);
    setFormOpen(true);
  };
  const openEdit = (p) => {
    setEditingProduct(p);
    setFormOpen(true);
  };
  const askDelete = (p) => setDeleteTarget(p);
  const confirmDelete = async () => {
    if (!deleteTarget) return;
    setDeleting(true);
    try {
      await adminProductsService.remove(deleteTarget.id);
      toast.success(t("admin.products.deleted"));
      setDeleteTarget(null);
      await loadProducts(1, true);
    } catch (err) {
      const d = err?.response?.data?.detail;
      if (d && typeof d === "object" && d.code === "IN_USE") {
        toast.error(t("admin.products.err_in_use"), {
          description: d.order?.order_number
            ? `#${d.order.order_number} · ${d.order.event_name || ""}`
            : undefined,
        });
      } else {
        toast.error(formatApiError(err, t("common.error_generic")));
      }
    } finally {
      setDeleting(false);
    }
  };

  const canLoadMore = items.length < total;
  const showSkeleton = loading && items.length === 0;

  return (
    <div className="space-y-6" data-testid="products-page">
      <header className="flex flex-col gap-3 md:flex-row md:items-end md:justify-between">
        <div>
          <h1
            className="text-3xl font-semibold tracking-tight text-text-primary"
            data-testid="products-title"
          >
            {t("products.title")}
          </h1>
          <p className="text-sm text-text-secondary">
            {t("products.subtitle")}
          </p>
        </div>
        {canManage && (
          <Button
            onClick={openCreate}
            data-testid="products-add-new"
            className="bg-charcoal text-white hover:bg-charcoal-soft focus-visible:ring-champagne"
          >
            <Plus className="mr-2 h-4 w-4" />
            {t("admin.products.add_new_cta")}
          </Button>
        )}
      </header>

      <ProductFilters
        search={searchInput}
        onSearchChange={setSearchInput}
        categories={facets.categories}
        brands={facets.brands}
        selectedCategories={selectedCategories}
        selectedBrands={selectedBrands}
        onToggleCategory={toggleCategory}
        onToggleBrand={toggleBrand}
        onClear={clearFilters}
        isFiltered={isFiltered}
      />

      {!loading && !error && (
        <div
          className="text-xs text-text-secondary"
          data-testid="products-results-count"
        >
          {total === 1
            ? t("products.results_count", { count: 1 })
            : t("products.results_count_plural", { count: total })}
        </div>
      )}

      {error && !loading ? (
        <ErrorState
          title={t("products.error_title")}
          body={error}
          onRetry={() => loadProducts(1, true)}
          testId="products-error"
        />
      ) : showSkeleton ? (
        <div
          className="grid gap-6 sm:grid-cols-2 lg:grid-cols-3"
          data-testid="products-skeleton"
        >
          {Array.from({ length: 5 }).map((_, i) => (
            <ProductCardSkeleton key={i} />
          ))}
        </div>
      ) : items.length === 0 ? (
        <EmptyState
          title={t("products.empty_title")}
          body={t("products.empty_body")}
          actionLabel={isFiltered ? t("products.clear_filters") : undefined}
          onAction={isFiltered ? clearFilters : undefined}
          testId="products-empty"
        />
      ) : (
        <>
          <div
            className="grid gap-6 sm:grid-cols-2 lg:grid-cols-3"
            data-testid="products-grid"
          >
            {items.map((p) => (
              <ProductCard
                key={p.id}
                product={p}
                onOpen={openProduct}
                onEdit={openEdit}
                onDelete={askDelete}
              />
            ))}
          </div>
          {canLoadMore && (
            <div className="flex justify-center pt-2">
              <Button
                variant="outline"
                onClick={() => loadProducts(page + 1, false)}
                disabled={loading}
                data-testid="products-load-more"
                className="border-border-subtle text-text-primary hover:bg-offwhite"
              >
                {loading ? t("common.loading") : t("products.load_more")}
              </Button>
            </div>
          )}
        </>
      )}

      <ProductDetailModal
        product={selected}
        open={detailOpen}
        onOpenChange={setDetailOpen}
      />

      {canManage && (
        <>
          <ProductFormModal
            open={formOpen}
            onOpenChange={setFormOpen}
            editingProduct={editingProduct}
            onSaved={() => loadProducts(1, true)}
          />
          <ConfirmDeleteModal
            open={Boolean(deleteTarget)}
            onClose={() => setDeleteTarget(null)}
            onConfirm={confirmDelete}
            name={deleteTarget?.name || ""}
            busy={deleting}
            testId="products-delete"
          />
        </>
      )}
    </div>
  );
}
